import { createCryptoCurrency } from '@0xtorch/core'
import { chiliz as viemChiliz } from 'viem/chains'
import { type Client, createClient } from '../../client'
import type { Explorer } from '../../explorer'
import { createRoutescan } from '../../explorer'
import type { LowerHex } from '../../types'
import type { Chain } from '../types/chain'
import type { HttpRpc } from '../types/rpc'

const chiliz = createCryptoCurrency({
  type: 'CryptoCurrency',
  id: 'chiliz',
  name: 'Chiliz',
  symbol: 'CHZ',
  market: {
    coingeckoId: 'chiliz',
  },
  updatedAt: 0,
})

/**
 * Chiliz Chain の標準設定を作成する。
 *
 * @param params - Explorer 接続設定
 * @param params.explorerApiKey - 任意の Routescan API key
 * @param params.explorerProxyUrl - Explorer API を中継するプロキシ URL
 * @param params.explorerPageSize - Explorer account 系 API のページサイズ
 * @returns Chiliz Chain 定義
 */
export const createChilizChain = ({
  explorerApiKey: apiKey,
  explorerProxyUrl: proxyUrl,
  explorerPageSize: pageSize,
}: {
  explorerApiKey?: string
  explorerProxyUrl?: string
  explorerPageSize?: number
}): Chain =>
  createChilizChainCustom({
    client: createClient({
      chain: viemChiliz,
      httpRpcs: chilizHttpRpcs,
    }),
    explorer: createRoutescan({
      name: 'Chiliscan',
      baseUrl: 'https://chiliscan.com',
      chainId: 88888,
      apiKey,
      proxyUrl,
      pageSize,
    }),
  })

type CreateChilizChainCustomParameters = {
  client: Client
  explorer: Explorer
}

/**
 * Chiliz Chain の client と explorer を差し替えて作成する。
 *
 * @param params - Chiliz Chain の依存実装
 * @param params.client - EVM RPC client
 * @param params.explorer - Explorer 実装
 * @returns Chiliz Chain 定義
 */
export const createChilizChainCustom = ({
  client,
  explorer,
}: CreateChilizChainCustomParameters): Chain => ({
  id: 88888,
  network: 'chiliz',
  name: 'Chiliz',
  nativeToken: {
    name: 'Chiliz',
    symbol: 'CHZ',
    decimals: 18,
    currency: chiliz,
  },
  wrappedTokenAddresses: new Set<LowerHex>([
    '0x677f7e16c7dd57be1d4c8ad1244883214953dc47',
  ]),
  explorer,
  client,
  blockTime: undefined,
  coingeckoId: 'chiliz',
  defillamaId: undefined,
})

export const chilizHttpRpcs: HttpRpc[] = [
  {
    url: 'https://rpc.chiliz.com',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 10_000n,
  },
  {
    url: 'https://rpc.ankr.com/chiliz/',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 10_000n,
  },
  {
    url: 'https://chiliz.publicnode.com',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 10_000n,
  },
  {
    url: 'https://chiliz-mainnet.gateway.tatum.io',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 10_000n,
  },
]

export const chilizWebsocketRpcUrls: readonly string[] = []
